import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FaUser, FaRightFromBracket, FaChevronDown, FaClipboardList, FaCloudArrowDown, FaGlobe } from 'react-icons/fa6';
import { useLogout } from '../../api/queryHooks/user';
import type { FlattenedUser } from '../../api/queryHooks/user';
import './UserIndicator.scss';

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' }
];

interface UserIndicatorProps {
  userData: FlattenedUser;
}

const UserIndicator = ({ userData }: UserIndicatorProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { mutate: logout } = useLogout();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfile = () => {
    setUserMenuOpen(false);
    navigate('/profile');
  };

  const handleOrders = () => {
    setUserMenuOpen(false);
    navigate('/orders');
  };

  const handleDownloads = () => {
    setUserMenuOpen(false);
    navigate('/downloads');
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setUserMenuOpen(false);
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.resolvedLanguage) || languages[0];

  return (
    <div className="user-menu-container" ref={userMenuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        aria-expanded={userMenuOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          <FaUser size={16} />
        </div>
        <span className="user-name">{userData.name}</span>
        <FaChevronDown size={12} className={`chevron ${userMenuOpen ? 'open' : ''}`} />
      </button>

      {userMenuOpen && (
        <div className="user-menu-dropdown">
          <button className="user-menu-item" onClick={handleProfile}>
            <FaUser size={16} />
            <span>{t('nav.profile')}</span>
          </button>
          <button className="user-menu-item" onClick={handleOrders}>
            <FaClipboardList size={16} />
            <span>{t('nav.orders')}</span>
          </button>
          <button className="user-menu-item" onClick={handleDownloads}>
            <FaCloudArrowDown size={16} />
            <span>{t('nav.downloads')}</span>
          </button>
          <button className="user-menu-item" onClick={handleLogout}>
            <FaRightFromBracket size={16} />
            <span>{t('nav.logout')}</span>
          </button>

          {/* Language selector - visible only on mobile */}
          <div className="user-menu-language-section">
            <div className="user-menu-divider" />
            <div className="user-menu-language-header">
              <FaGlobe size={14} />
              <span>{currentLanguage.flag} {currentLanguage.code.toUpperCase()}</span>
            </div>
            {languages.map(lang => (
              <button
                key={lang.code}
                className={`user-menu-item user-menu-language-item ${lang.code === i18n.resolvedLanguage ? 'is-active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="language-flag">{lang.flag}</span>
                <span>{lang.name}</span>
                {lang.code === i18n.resolvedLanguage && (
                  <span className="language-check">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserIndicator;
